
import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Monitor, Smartphone, ArrowRight, Sparkles, Save, Calculator, DollarSign, Home, Percent, TrendingUp, Shield, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { NumericField } from '@/components/ui/NumericField';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { ValidationSummaryPanel } from '@/components/ui/ValidationSummaryPanel';
import { SectionProgressIndicator, createReversePitiSections } from '@/components/ui/SectionProgressIndicator';
import { useReversePitiState, VAStatus, VADisabilityStatus } from '@/hooks/calculator/useReversePitiState';
import { useCalculatorValidation } from '@/hooks/useCalculatorValidation';
import { useSoundEffects } from '@/hooks/useSoundEffects';
import { useAuth } from '@/contexts/SupabaseAuthContext';
import { images } from '@/assets/images';
import { cn } from '@/lib/utils';
import { detectMobileImmediate } from '@/hooks/useMobileRedirect';
import { LoanTypeOption } from '@/components/calculator/utils/types';
import { CalculatorNav } from '@/components/calculator/CalculatorNav';
import { RateSourceIndicator } from '@/components/calculator/RateSourceIndicator';
import { CalculatorResetButton } from '@/components/calculator/CalculatorResetButton';
import { useCalculatorReset } from '@/hooks/calculator/useCalculatorReset';
import { useCalculatorExport } from '@/hooks/calculator/useCalculatorExport';
import { StateExportImport } from '@/components/calculator/StateExportImport';

import { MobileReversePitiView } from '@/components/calculator/mobile/MobileReversePitiContent';
import { PageTransition } from '@/components/layout/PageTransition';

type ViewMode = 'desktop' | 'mobile';

const TryReversePiti: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  
  // Initialize with immediate mobile detection to prevent flash
  const [viewMode, setViewMode] = useState<ViewMode>(() => 
    detectMobileImmediate() ? 'mobile' : 'desktop'
  );
  const [isAutoDetected, setIsAutoDetected] = useState(true);
  
  // Keep one state owner while folding or rotating changes the layout.
  const calculator = useReversePitiState();
  const {
    loanType,
    vaStatus,
    vaDisabilityStatus,
    vaDisabilityRatingPercentage,
    desiredPiti,
    formattedInterestRate,
    loanTerm,
    downPaymentPercent,
    propertyTaxRate,
    annualInsurance,
    hoaFees,
    pmiRate,
    includePmi,
    setDesiredPiti,
    setInterestRate,
    setLoanTerm,
    setDownPaymentPercent,
    setPropertyTaxRate,
    setAnnualInsurance,
    setHoaFees,
    setPmiRate,
    setIncludePmi,
    handleLoanTypeChange,
    handleVAStatusChange,
    handleVADisabilityStatusChange,
    handleVADisabilityPercentageChange,
    isUsingLiveRate,
    results,
    formatCurrency,
    formatCurrencyDecimal,
    resetToDefaults,
    getExportableState,
    loadFromExport,
  } = calculator;

  // Calculator reset hook
  const { resetCalculator, isResetting } = useCalculatorReset({
    formSlug: 'reverse_piti',
    onReset: resetToDefaults,
  });

  // Calculator export/import hook
  const { exportState, importState } = useCalculatorExport({
    calculatorType: 'reverse-piti',
    getState: getExportableState,
    loadState: loadFromExport,
  });

  // Calculator validation hook
  const validation = useCalculatorValidation({
    desiredPiti,
    interestRate: formattedInterestRate,
    loanTerm,
    downPaymentPercent,
    propertyTaxRate,
    annualInsurance,
    hoaFees,
    pmiRate,
    loanType,
    includePmi,
  });

  // Sound effects for accessibility
  const { playSound } = useSoundEffects();

  // Section progress indicator data
  const sectionStatuses = createReversePitiSections({
    desiredPiti,
    interestRate: formattedInterestRate,
    loanTerm,
    downPaymentPercent,
    propertyTaxRate,
    annualInsurance,
    hoaFees,
    pmiRate,
    includePmi,
  });

  // Field refs for scroll-to-error
  const fieldRefs = useRef<Record<string, HTMLElement | null>>({});

  const scrollToField = (field: string) => {
    const el = fieldRefs.current[field];
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.focus?.();
    }
    validation.setShowSummary(false);
  };

  // Handle validation with sound effects
  const handleValidateAndCalculate = () => {
    if (validation.validate()) {
      playSound('success');
      return true;
    } else {
      playSound('error');
      return false;
    }
  };

  const hasCalculation = results?.maxHomePrice > 0;

  // Auto-detect screen size on mount
  useEffect(() => {
    const detectScreenSize = () => {
      const isMobile = window.innerWidth < 768;
      if (isAutoDetected) {
        setViewMode(isMobile ? 'mobile' : 'desktop');
      }
    };

    detectScreenSize();
    window.addEventListener('resize', detectScreenSize);
    return () => window.removeEventListener('resize', detectScreenSize);
  }, [isAutoDetected]);

  const handleModeSwitch = (mode: ViewMode) => {
    setIsAutoDetected(false);
    setViewMode(mode);
  };

  return (
    <PageTransition>
    <div className="duo-page min-h-dvh bg-gradient-to-br from-slate-950 via-purple-950/20 to-slate-950 flex flex-col">
      {/* Compact Header */}
      <header className="calculator-header sticky top-0 z-50 bg-slate-950/80 backdrop-blur-xl border-b border-border/40">
        <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 max-w-7xl mx-auto">
          {/* Logo */}
          <Link to="/home" className="flex items-center gap-2">
            <img
              src={images.equiforgeLogoFlat}
              alt="EquiForge"
              className="h-8 md:h-10 w-auto object-contain"
            />
          </Link>

          {/* View Toggle & Actions */}
          <div className="calculator-toolbar flex flex-wrap items-center gap-2">
            {/* Calculator Navigation */}
            <CalculatorNav />

            <div className="flex bg-slate-800/50 rounded-lg p-1 border border-border/40">
              <button aria-label="Automatically adapt layout" aria-pressed={isAutoDetected} onClick={() => setIsAutoDetected(true)} className={cn("min-h-11 px-3 rounded-md text-sm", isAutoDetected ? "bg-primary text-primary-foreground" : "text-muted-foreground")}>Auto</button>
              <button
                aria-label="Use desktop layout"
                aria-pressed={viewMode === 'desktop'}
                onClick={() => handleModeSwitch('desktop')}
                className={cn(
                  "flex min-h-11 min-w-11 items-center justify-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all",
                  viewMode === 'desktop'
                    ? "bg-primary text-primary-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground hover:bg-slate-700/50"
                )}
              >
                <Monitor className="w-4 h-4" />
                <span className="hidden lg:inline">Desktop</span>
              </button>
              <button
                aria-label="Use mobile layout"
                aria-pressed={viewMode === 'mobile'}
                onClick={() => handleModeSwitch('mobile')}
                className={cn(
                  "flex min-h-11 min-w-11 items-center justify-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all",
                  viewMode === 'mobile'
                    ? "bg-primary text-primary-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground hover:bg-slate-700/50"
                )}
              >
                <Smartphone className="w-4 h-4" />
                <span className="hidden lg:inline">Mobile</span>
              </button>
            </div>

            {/* Mobile view provides its own reset and import/export controls. */}
            <div className="hidden md:flex items-center gap-2">
            <CalculatorResetButton
              onReset={resetCalculator}
              isResetting={isResetting}
              variant="compact"
            />

            {/* Export/Import Button */}
            <StateExportImport
              onExport={exportState}
              onImport={importState}
              variant="compact"
            />

            </div>

            {/* Save Button */}
            {hasCalculation && (
              <Button 
                onClick={() => {
                  if (!isAuthenticated) {
                    navigate('/login', { state: { returnTo: '/try/reverse-piti' } });
                  }
                }} 
                variant="outline" 
                size="sm" 
                className="border-border/60 bg-slate-800/50 hover:bg-slate-700/50"
              >
                <Save className="w-4 h-4 mr-1.5" />
                <span className="hidden lg:inline">{isAuthenticated ? 'Save' : 'Sign in to Save'}</span>
                <span className="lg:hidden">Save</span>
              </Button>
            )}

            {/* Sign In Button - only show if not authenticated */}
            {!isAuthenticated && (
              <Button asChild variant="outline" size="sm" className="hidden lg:flex border-border/60">
                <Link to="/login">Sign In</Link>
              </Button>
            )}
          </div>
        </div>
      </header>

      {/* Calculator Content */}
      <main className="flex-1 relative">
        <AnimatePresence mode="wait">
          {viewMode === 'desktop' ? (
            <motion.div
              key="desktop"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ duration: 0.3 }}
              className="h-full"
            >
              <div className="max-w-7xl mx-auto px-4 py-6">
                {/* Header */}
                <div className="flex flex-col gap-2 mb-6">
                  <h2 className="text-2xl font-bold flex items-center gap-2 text-foreground">
                    <TrendingUp className="w-6 h-6 text-primary" />
                    Reverse PITI Calculator
                  </h2>
                  <p className="text-muted-foreground">
                    Enter your desired monthly PITI payment to find out the maximum home price you can afford
                  </p>
                </div>

                {/* Validation Summary Panel */}
                <ValidationSummaryPanel
                  errors={validation.errors}
                  isVisible={validation.showSummary}
                  onDismiss={() => validation.setShowSummary(false)}
                  onFieldClick={scrollToField}
                  className="mb-6"
                />

                {/* Section Progress Indicator */}
                <Card className="mb-6">
                  <CardContent className="p-4">
                    <SectionProgressIndicator sections={sectionStatuses} />
                  </CardContent>
                </Card>
                <div className="grid grid-cols-1 min-[840px]:grid-cols-2 gap-6">
                  {/* Input Section */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Calculator className="w-5 h-5" />
                        Input Parameters
                      </CardTitle>
                      <CardDescription>
                        Adjust the values to calculate your maximum purchase price
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      {/* Desired PITI - Main Input */}
                      <div className="p-4 bg-primary/5 border border-primary/20 rounded-lg">
                        <Label htmlFor="desiredPiti" className="text-base font-semibold flex items-center gap-2">
                          <DollarSign className="w-4 h-4" />
                          Desired Monthly PITI Payment
                        </Label>
                        <div className="mt-2 relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                          <NumericField
                            id="desiredPiti"
                            value={desiredPiti}
                            onChange={setDesiredPiti}
                            className="pl-8 text-lg font-semibold"
                            min={0}
                            step={100}
                          />
                        </div>
                      </div>

                      {/* Loan Type Selector */}
                      <div className="space-y-2">
                        <Label className="text-sm font-medium">Loan Type</Label>
                        <Select value={loanType} onValueChange={(val) => handleLoanTypeChange(val as LoanTypeOption)}>
                          <SelectTrigger className="bg-background">
                            <SelectValue placeholder="Select loan type" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="conventional">Conventional</SelectItem>
                            <SelectItem value="fha">FHA</SelectItem>
                            <SelectItem value="va">VA</SelectItem>
                            <SelectItem value="usda">USDA</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {/* VA Loan Options */}
                      {loanType === 'va' && (
                        <div className="p-4 bg-blue-50/50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-lg space-y-4">
                          <div className="flex items-center gap-2">
                            <Shield className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                            <Label className="text-sm font-medium text-blue-800 dark:text-blue-300">
                              VA Loan Details
                            </Label>
                          </div>
                          
                          {/* Military Status */}
                          <div className="space-y-2">
                            <Label className="text-sm font-medium">Military Status</Label>
                            <Select 
                              value={vaStatus || ""} 
                              onValueChange={(value) => handleVAStatusChange(value as VAStatus)}
                            >
                              <SelectTrigger className="bg-background">
                                <SelectValue placeholder="Select military status" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="activeDuty">Active Duty</SelectItem>
                                <SelectItem value="separatedRetired">Separated/Retired</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>

                          {/* VA Disability Status - Only show if Separated/Retired */}
                          {vaStatus === 'separatedRetired' && (
                            <div className="space-y-2">
                              <Label className="text-sm font-medium">VA Disability Rating</Label>
                              <Select 
                                value={vaDisabilityStatus || ""} 
                                onValueChange={(value) => handleVADisabilityStatusChange(value as VADisabilityStatus)}
                              >
                                <SelectTrigger className="bg-background">
                                  <SelectValue placeholder="Select disability rating" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="none">No Rating</SelectItem>
                                  <SelectItem value="partial">10% to 90%</SelectItem>
                                  <SelectItem value="full">100%</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          )}

                          {/* Disability Rating Percentage Slider - Only for partial */}
                          {vaStatus === 'separatedRetired' && vaDisabilityStatus === 'partial' && (
                            <div className="space-y-3 p-3 bg-muted/50 rounded-lg">
                              <div className="flex items-center justify-between">
                                <Label className="text-sm font-medium">Exact Disability Rating</Label>
                                <span className="text-sm font-semibold text-primary">{vaDisabilityRatingPercentage}%</span>
                              </div>
                              <div className="flex items-center gap-3">
                                <Slider
                                  value={[vaDisabilityRatingPercentage]}
                                  onValueChange={(values) => handleVADisabilityPercentageChange(values[0])}
                                  min={10}
                                  max={90}
                                  step={10}
                                  className="flex-1"
                                />
                                <Input
                                  type="number"
                                  value={vaDisabilityRatingPercentage}
                                  onChange={(e) => handleVADisabilityPercentageChange(Math.min(90, Math.max(10, parseInt(e.target.value) || 10)))}
                                  onFocus={(e) => e.target.select()}
                                  min={10}
                                  max={90}
                                  step={10}
                                  className="w-20"
                                />
                              </div>
                              <p className="text-xs text-muted-foreground">
                                Property tax reduced by {vaDisabilityRatingPercentage}% based on your disability rating
                              </p>
                            </div>
                          )}

                          {/* Benefits Information */}
                          {(vaDisabilityStatus === 'partial' || vaDisabilityStatus === 'full') && (
                            <div className="p-3 bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800 rounded-lg">
                              <div className="flex items-start gap-2">
                                <Info size={16} className="text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
                                <div className="space-y-1">
                                  <p className="text-sm text-green-800 dark:text-green-300 font-medium">
                                    VA Disability Benefits Applied:
                                  </p>
                                  <ul className="text-xs text-green-700 dark:text-green-300 space-y-1">
                                    <li>• 0% down payment required</li>
                                    <li>• VA funding fee waived</li>
                                    {vaDisabilityStatus === 'full' && (
                                      <li>• Potential property tax exemptions (varies by state)</li>
                                    )}
                                  </ul>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Active Duty Benefits */}
                          {vaStatus === 'activeDuty' && (
                            <div className="p-3 bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg">
                              <div className="flex items-start gap-2">
                                <Info size={16} className="text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                                <div className="space-y-1">
                                  <p className="text-sm text-blue-800 dark:text-blue-300 font-medium">
                                    Active Duty Benefits:
                                  </p>
                                  <ul className="text-xs text-blue-700 dark:text-blue-300 space-y-1">
                                    <li>• 0% down payment option</li>
                                    <li>• Reduced VA funding fee (2.15%)</li>
                                    <li>• No PMI required</li>
                                  </ul>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      <div className="grid grid-cols-2 gap-4">
                        {/* Interest Rate */}
                        <div>
                          <Label htmlFor="interestRate">Interest Rate (%)</Label>
                          <div className="mt-1 relative">
                            <NumericField
                              id="interestRate"
                              value={formattedInterestRate}
                              onChange={setInterestRate}
                              step={0.125}
                              min={0}
                              max={20}
                            />
                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">%</span>
                          </div>
                          <RateSourceIndicator isLive={isUsingLiveRate} className="mt-1" />
                        </div>

                        {/* Loan Term */}
                        <div>
                          <Label htmlFor="loanTerm">Loan Term</Label>
                          <Select
                            value={String(loanTerm)}
                            onValueChange={(val) => setLoanTerm(Number(val))}
                          >
                            <SelectTrigger className="mt-1">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="30">30 years</SelectItem>
                              <SelectItem value="20">20 years</SelectItem>
                              <SelectItem value="15">15 years</SelectItem>
                              <SelectItem value="10">10 years</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        {/* Down Payment */}
                        <div>
                          <Label htmlFor="downPayment">Down Payment (%)</Label>
                          <div className="mt-1 relative">
                            <NumericField
                              id="downPayment"
                              value={downPaymentPercent}
                              onChange={setDownPaymentPercent}
                              step={1}
                              min={0}
                              max={100}
                            />
                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">%</span>
                          </div>
                        </div>

                        {/* Property Tax Rate */}
                        <div>
                          <Label htmlFor="propertyTaxRate">Property Tax Rate (%)</Label>
                          <div className="mt-1 relative">
                            <NumericField
                              id="propertyTaxRate"
                              value={propertyTaxRate}
                              onChange={setPropertyTaxRate}
                              step={0.1}
                              min={0}
                              max={10}
                            />
                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">%</span>
                          </div>
                        </div>

                        {/* Annual Insurance */}
                        <div>
                          <Label htmlFor="annualInsurance">Annual Insurance ($)</Label>
                          <div className="mt-1 relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                            <NumericField
                              id="annualInsurance"
                              value={annualInsurance}
                              onChange={setAnnualInsurance}
                              step={100}
                              min={0}
                              className="pl-8"
                            />
                          </div>
                        </div>

                        {/* HOA Fees */}
                        <div>
                          <Label htmlFor="hoaFees">Monthly HOA ($)</Label>
                          <div className="mt-1 relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                            <NumericField
                              id="hoaFees"
                              value={hoaFees}
                              onChange={setHoaFees}
                              step={25}
                              min={0}
                              className="pl-8"
                            />
                          </div>
                        </div>
                      </div>

                      {/* PMI Toggle */}
                      <div className="p-4 bg-muted/30 rounded-lg space-y-4">
                        <div className="flex items-center justify-between">
                          <div className="space-y-0.5">
                            <Label htmlFor="includePmi" className="text-sm font-medium">Include PMI</Label>
                            <p className="text-xs text-muted-foreground">
                              Private Mortgage Insurance (typically required if down payment {'<'} 20%)
                            </p>
                          </div>
                          <Switch
                            id="includePmi"
                            checked={includePmi}
                            onCheckedChange={setIncludePmi}
                            disabled={loanType === 'va' || loanType === 'usda'}
                          />
                        </div>

                        {includePmi && (
                          <div>
                            <Label htmlFor="pmiRate">PMI Rate (%)</Label>
                            <div className="mt-1 relative">
                              <NumericField
                                id="pmiRate"
                                value={pmiRate}
                                onChange={setPmiRate}
                                step={0.1}
                                min={0}
                                max={5}
                              />
                              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">%</span>
                            </div>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Results Section */}
                  <div className="space-y-6">
                    {/* Main Result Card */}
                    <Card className="border-primary/30 bg-gradient-to-br from-primary/5 to-primary/10">
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <Home className="w-5 h-5 text-primary" />
                          Maximum Home Price
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="text-center">
                          <p className="text-5xl font-bold text-primary">
                            {formatCurrency(results.maxHomePrice)}
                          </p>
                          <p className="text-sm text-muted-foreground mt-2">
                            Based on your desired monthly payment of {formatCurrency(desiredPiti)}
                          </p>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Cost Breakdown Card */}
                    <Card>
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <Percent className="w-5 h-5" />
                          Monthly Payment Breakdown
                        </CardTitle>
                        <CardDescription>
                          How your {formatCurrency(desiredPiti)}/month payment is allocated
                        </CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="space-y-3">
                          <div className="flex justify-between items-center p-3 bg-blue-50/50 dark:bg-blue-950/20 rounded-lg">
                            <span className="text-sm font-medium">Principal & Interest</span>
                            <span className="font-semibold">{formatCurrencyDecimal(results.monthlyPrincipalInterest)}</span>
                          </div>
                          <div className="flex justify-between items-center p-3 bg-purple-50/50 dark:bg-purple-950/20 rounded-lg">
                            <span className="text-sm font-medium">Property Taxes</span>
                            <span className="font-semibold">{formatCurrencyDecimal(results.monthlyPropertyTax)}</span>
                          </div>
                          <div className="flex justify-between items-center p-3 bg-amber-50/50 dark:bg-amber-950/20 rounded-lg">
                            <span className="text-sm font-medium">Insurance</span>
                            <span className="font-semibold">{formatCurrencyDecimal(results.monthlyInsurance)}</span>
                          </div>
                          {hoaFees > 0 && (
                            <div className="flex justify-between items-center p-3 bg-green-50/50 dark:bg-green-950/20 rounded-lg">
                              <span className="text-sm font-medium">HOA Fees</span>
                              <span className="font-semibold">{formatCurrencyDecimal(results.monthlyHoa)}</span>
                            </div>
                          )}
                          {includePmi && results.monthlyPmi > 0 && (
                            <div className="flex justify-between items-center p-3 bg-red-50/50 dark:bg-red-950/20 rounded-lg">
                              <span className="text-sm font-medium">PMI</span>
                              <span className="font-semibold">{formatCurrencyDecimal(results.monthlyPmi)}</span>
                            </div>
                          )}
                          <div className="flex justify-between items-center p-3 bg-muted rounded-lg border-2 border-primary/20">
                            <span className="text-sm font-semibold">Total PITI</span>
                            <span className="font-bold text-primary">{formatCurrencyDecimal(results.totalPiti)}</span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Loan Details Card */}
                    <Card>
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <Calculator className="w-5 h-5" />
                          Loan Details
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="p-3 bg-muted/30 rounded-lg">
                            <p className="text-xs text-muted-foreground">Loan Amount</p>
                            <p className="font-semibold">{formatCurrency(results.loanAmount)}</p>
                          </div>
                          <div className="p-3 bg-muted/30 rounded-lg">
                            <p className="text-xs text-muted-foreground">Down Payment</p>
                            <p className="font-semibold">{formatCurrency(results.downPayment)}</p>
                          </div>
                          <div className="p-3 bg-muted/30 rounded-lg">
                            <p className="text-xs text-muted-foreground">Interest Rate</p>
                            <p className="font-semibold">{formattedInterestRate}%</p>
                          </div>
                          <div className="p-3 bg-muted/30 rounded-lg">
                            <p className="text-xs text-muted-foreground">Loan Term</p>
                            <p className="font-semibold">{loanTerm} years</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </div>
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="mobile"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
              className="h-full"
            >
              <MobileReversePitiView calculator={calculator} />
            </motion.div>
          )}
        </AnimatePresence>
      </main>

      {/* Floating CTA Banner */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 2, duration: 0.5 }}
        className="calculator-promo relative z-40 bg-gradient-to-r from-purple-900/90 via-slate-900/95 to-cyan-900/90 backdrop-blur-xl border-t border-border/40"
      >
        <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 max-w-7xl mx-auto">
          <div className="flex items-center gap-2 text-sm">
            <Sparkles className="w-4 h-4 text-purple-400" />
            <span className="text-slate-300">
              <span className="hidden sm:inline">Want SmartBuy comparison, AI insights & more? </span>
              <span className="sm:hidden">Unlock all tools </span>
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Button asChild size="sm" className="bg-gradient-to-r from-purple-600 to-cyan-600 hover:from-purple-700 hover:to-cyan-700">
              <Link to="/signup">
                Sign Up Free
                <ArrowRight className="ml-1.5 w-4 h-4" />
              </Link>
            </Button>
            <Button asChild variant="ghost" size="sm" className="text-slate-400 hover:text-slate-200 sm:flex hidden">
              <Link to="/home">Learn More</Link>
            </Button>
          </div>
        </div>
      </motion.div>

    </div>
    </PageTransition>
  );
};

export default TryReversePiti;
